import { useNavigate } from 'react-router'
import Popover from '@mui/material/Popover'
import List from '@mui/material/List'
import Button from '@mui/material/Button'
import Typography from '@mui/material/Typography'
import Box from '@mui/material/Box'
import Divider from '@mui/material/Divider'
import { useNotificationStore } from '../../stores/notificationStore'
import { NotificationItem } from '../NotificationItem'
import { getNavigationTarget } from '../../utils/notificationNavigation'

interface NotificationCenterProps {
  anchorEl: HTMLElement | null
  onClose: () => void
}

export default function NotificationCenter({
  anchorEl,
  onClose
}: NotificationCenterProps) {
  const navigate = useNavigate()
  const notifications = useNotificationStore(state => state.notifications)
  const markAsRead = useNotificationStore(state => state.markAsRead)
  const markAllAsRead = useNotificationStore(state => state.markAllAsRead)
  const dismiss = useNotificationStore(state => state.dismiss)

  const open = anchorEl !== null

  const handleMarkAllAsRead = () => {
    markAllAsRead()
  }

  const handleDismiss = (id: string) => {
    dismiss(id)
  }

  const handleMarkRead = (id: string) => {
    markAsRead(id)
  }

  const handleNavigate = (path: string) => {
    navigate(path)
    onClose()
  }

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      slotProps={{
        paper: {
          sx: { width: 360, maxHeight: 480 }
        }
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2,
          py: 1
        }}
      >
        <Typography variant='subtitle1' fontWeight={600}>
          Notifications
        </Typography>
        <Button size='small' onClick={handleMarkAllAsRead}>
          Mark all as read
        </Button>
      </Box>
      <Divider />
      {notifications.length === 0 ? (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            py: 4
          }}
        >
          <Typography color='text.secondary'>No notifications</Typography>
        </Box>
      ) : (
        <List disablePadding sx={{ maxHeight: 400, overflowY: 'auto' }}>
          {notifications.map(notification => (
            <NotificationItem
              key={notification.id}
              notification={notification}
              onMarkRead={handleMarkRead}
              onDismiss={handleDismiss}
              onNavigate={handleNavigate}
            />
          ))}
        </List>
      )}
    </Popover>
  )
}
